import { useQuery, type ApolloError } from '@apollo/client'
import { useParams } from 'react-router-dom'
import {
  GET_CHECKOUT_RULE,
  type CheckoutRuleOption,
  type CheckoutRuleType,
  type IGetCheckoutRule,
  type IGetCheckoutRuleVariables,
} from '@commerce/core'

export interface UseCheckoutRuleOptions {
  options: CheckoutRuleOption[]
  loading: boolean
  error: ApolloError | undefined
  refetch: () => Promise<unknown>
}

/**
 * The options the client system offers this shopper right now — delivery
 * dates or payment methods, resolved live through the GET_CHECKOUT_RULE
 * add-on.
 *
 * `network-only` because the list is *recomputed on every read* by the
 * client's own system: credit that changes intraday, delivery windows with
 * cutoffs. A cached answer is exactly the stale list the server will then
 * reject a selection against.
 */
export const useCheckoutRuleOptions = (ruleType: CheckoutRuleType): UseCheckoutRuleOptions => {
  const { slug = '', sessionId = '' } = useParams()

  const { data, loading, error, refetch } = useQuery<IGetCheckoutRule, IGetCheckoutRuleVariables>(
    GET_CHECKOUT_RULE,
    {
      variables: { storefrontName: slug, sessionUid: sessionId, ruleType },
      fetchPolicy: 'network-only',
    },
  )

  return { options: data?.getCheckoutRule?.data ?? [], loading, error, refetch }
}
